import React, { useState, useEffect } from "react";
import Toast from "./Toast";
import { ToastService } from "./toastService";

const ToastContainer = ({ screenName }) => {
  const [activeToasts, setActiveToasts] = useState([]);
  const [displayedToasts, setDisplayedToasts] = useState(new Set());
  const [lastRefresh, setLastRefresh] = useState(0);

  useEffect(() => {
    const loadToasts = async () => {
      try {
        const toasts = await ToastService.getActiveToasts(screenName);

        // Filter out toasts that are already being displayed
        const newToasts = toasts.filter(
          (toast) => !displayedToasts.has(toast.id)
        );

        if (newToasts.length > 0) {
          setActiveToasts(newToasts);

          // Mark as displayed
          const newDisplayedSet = new Set(displayedToasts);
          newToasts.forEach((toast) => {
            newDisplayedSet.add(toast.id);
            ToastService.markAsViewed(toast.id);
          });
          setDisplayedToasts(newDisplayedSet);

          // Auto-dismiss toasts with duration
          newToasts.forEach((toast) => {
            if (toast.duration && !toast.dismissible) {
              setTimeout(() => {
                handleDismiss(toast.id);
              }, toast.duration);
            }
          });
        }
      } catch (error) {
        console.error("Failed to load toasts:", error);
      }
    };

    loadToasts();

    // Listen for global refresh events (triggered when ConfigMaps are updated)
    const handleGlobalRefresh = () => {
      setLastRefresh(Date.now());
      setDisplayedToasts(new Set()); // Clear displayed toasts to allow re-showing
      loadToasts();
    };

    window.addEventListener("toastConfigUpdated", handleGlobalRefresh);

    // Set up periodic refresh every 5 minutes to check for ConfigMap updates
    const refreshInterval = setInterval(() => {
      const now = Date.now();
      // Only refresh if it's been at least 5 minutes since last refresh
      if (now - lastRefresh > 300000) {
        // 5 minutes
        setLastRefresh(now);
        // Clear displayed toasts to allow re-showing updated toasts
        setDisplayedToasts(new Set());
        loadToasts();
      }
    }, 300000); // Check every 5 minutes

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener("toastConfigUpdated", handleGlobalRefresh);
    };
  }, [screenName, displayedToasts, lastRefresh]);

  const handleDismiss = (toastId) => {
    ToastService.dismissToast(toastId);
    setActiveToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  };

  if (activeToasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "120px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1200,
        pointerEvents: "none", // Allow clicks through the container
      }}
    >
      {activeToasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            marginBottom: index < activeToasts.length - 1 ? "10px" : "0",
            pointerEvents: "auto", // Enable clicks on individual toasts
            position: "relative",
          }}
        >
          <Toast toast={toast} onDismiss={handleDismiss} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
